import { TooltipFormatterContextObject } from 'highcharts';
import { ReactNode } from 'react';
import {
  ProjectFinancialsApiData,
  ProjectFinancialsBudgetBreakdown,
  ProjectFinancialsHoursBreakdown,
  ProjectFinancialsZoomLevel,
  ProjectFinancialsContractType,
  ProjectFinancialsLineChartApiData,
  ProjectFinancialsBarChartApiData,
  ProjectFinancialsGroupBy,
} from 'api/project';

export enum ProjectFinancialsComponentGroup {
  Budget = 'BUDGET',
  Hours = 'HOURS',
}

export enum ProjectFinancialsComponentType {
  BudgetLine = 'BUDGET_LINE',
  BudgetBar = 'BUDGET_BAR',
  HoursBar = 'HOURS_BAR',
  BudgetTable = 'BUDGET_TABLE',
  HoursTable = 'HOURS_TABLE',
}

export enum ProjectFinancialsGaugeValueRange {
  InRange = 'InRange',
  OutOfRange = 'OutOfRange',
}

export interface ProjectFinancialsVisibilityConfig {
  profitabilityData?: boolean;
  allocationData?: boolean;
}

export type ProjectFinancialsComponentZoomLevelMap = {
  [Property in ProjectFinancialsComponentType]: ProjectFinancialsZoomLevel[];
};

export type ProjectFinancialsTypeZoomLevelMap = {
  [Property in ProjectFinancialsContractType]: ProjectFinancialsComponentZoomLevelMap;
};

export type ProjectFinancialsTypeGaugeChartColorMap = {
  [Property in ProjectFinancialsContractType]: {
    [Prop in ProjectFinancialsComponentGroup]: {
      [ProperT in ProjectFinancialsGaugeValueRange]: string;
    };
  };
};

export type ProjectFinancialsTypeLineChartColorMap = {
  [Property in ProjectFinancialsContractType]: {
    [K in keyof Omit<ProjectFinancialsLineChartApiData, 'startDate'>]: string;
  };
};

export type ProjectFinancialsTypeBarChartColorMap = {
  [Property in ProjectFinancialsContractType]: {
    [K in keyof Omit<
      ProjectFinancialsBarChartApiData,
      'startDate' | 'endDate'
    >]: string;
  };
};

export interface FinancialStat {
  label: ReactNode;
  value: ReactNode;
}

export interface ProjectFinancialsGaugeChartProps {
  type: ProjectFinancialsComponentGroup;
  value: number;
  max: number;
  remainingValue: number;
  valueLabel: string;
  maxLabel: string;
  remainingLabel: string;
}

export interface ProjectFinancialsCardBase {
  type: ProjectFinancialsComponentGroup;
}

export type ProjectFinancialsBudgetCard = ProjectFinancialsBudgetBreakdown &
  ProjectFinancialsCardBase;

export type ProjectFinancialsHoursCard = Pick<
  ProjectFinancialsHoursBreakdown,
  'timeAllocated'
> &
  Pick<
    ProjectFinancialsApiData,
    'billableTimeTracked' | 'nonBillableTimeTracked'
  > &
  ProjectFinancialsCardBase;

export interface ProjectFinancialsGroupByParams {
  options: ProjectFinancialsGroupBy[];
  selectedOption: ProjectFinancialsGroupBy;
  onGroupByChange: (value: ProjectFinancialsGroupBy) => void;
}

export interface ProjectFinancialsZoomLevelSwitcherParams {
  tabs: ProjectFinancialsZoomLevel[];
  activeTab: ProjectFinancialsZoomLevel;
  onTabSwitch: (value: ProjectFinancialsZoomLevel) => void;
}

export interface ProjectFinancialsTableNameCellRendererParams {
  prefix?: ReactNode;
  suffix?: ReactNode;
}

export interface ProjectFinancialsBaseChartProps {
  currencyCode: string;
  revenueLabels: string[];
  timeLabels: string[];
  percentageLabels: string[];
}

export interface ProjectFinancialsChartTooltipProps
  extends ProjectFinancialsBaseChartProps {
  x?: number;
  points: TooltipFormatterContextObject[];
  maxValue?: number;
  maxLabel?: string;
  remainingValue?: number;
  remainingLabel?: string;
}

export interface ProjectFinancialsChartLegendProps {
  color: string;
  shape: 'circle' | 'line' | 'dashed';
}
